import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * WHO WOULD APPROVE A MASTER CHANGE I FILED RIGHT NOW.
 *
 * Read by every confirm dialog that FILES rather than DOES, so the dialog can
 * name the destination before the act — "This will be sent to <person> for
 * approval" — with no role literal in the JSX.
 *
 * `/master-changes/approver` resolves it through `team_scope.waiting_on`, the
 * same function that stamps the filed row and the pending card, on a row shaped
 * exactly as the filing would write it. So the dialog cannot promise an approver
 * the filing would not route to.
 *
 * Fetched once per mount rather than per dialog open: the answer depends on the
 * viewer's role and the approving role's roster, neither of which changes while
 * somebody is looking at a page. `null` means "not answered yet" and the caller
 * shows nothing rather than a guess — no fallback role string, for the reason
 * every list on this app is backend-served.
 */
export default function useMasterChangeApprover(enabled = true) {
  const [waitingOn, setWaitingOn] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setWaitingOn(null);
      return;
    }
    try {
      const { data } = await api.get("/master-changes/approver");
      // A SHAPE CHECK, not a truthiness check. An unwired mock — or a proxy
      // answering `[]` — is truthy, and `approverPhrase` would render it as
      // "nobody — no approver could be resolved", which is the honest answer to
      // a DIFFERENT question and reads as a real one. No answer means show
      // nothing.
      setWaitingOn(data && data.approver_role !== undefined ? data : null);
    } catch {
      // A caller who cannot file master changes gets a 403 here, which is the
      // correct answer and not an error worth a banner: the dialog simply has
      // no destination to name, and the filing itself would refuse too.
      setWaitingOn(null);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { waitingOn, refresh };
}
